import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Package, Inbox, LogOut, Plus, Pencil, Trash2, Mail, MailOpen, ExternalLink, Upload, Image as ImageIcon, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const CATS = [
  { key: "raw", label: "Hammadde" },
  { key: "food", label: "Gıda" },
  { key: "spices", label: "Baharat" },
];

const emptyProduct = {
  name: { tr: "", en: "", ru: "" },
  description: { tr: "", en: "", ru: "" },
  category: "raw",
  brand: "",
  image: "",
  order: 0,
};

// Client-side image compression -> returns a compact base64 data URL.
function compressImage(file, maxSize = 1200, quality = 0.8) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > height && width > maxSize) {
          height = Math.round((height * maxSize) / width);
          width = maxSize;
        } else if (height > maxSize) {
          width = Math.round((width * maxSize) / height);
          height = maxSize;
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function AdminDashboard() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProduct);
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const dataUrl = await compressImage(file);
      setForm((f) => ({ ...f, image: dataUrl }));
      toast.success("Görsel yüklendi");
    } catch {
      toast.error("Görsel yüklenemedi");
    } finally {
      setUploading(false);
    }
  };

  const loadProducts = () => api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  const loadInquiries = () => api.get("/inquiries").then(({ data }) => setInquiries(data)).catch(() => {});

  useEffect(() => {
    loadProducts();
    loadInquiries();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(emptyProduct);
    setDialogOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: { ...emptyProduct.name, ...p.name },
      description: { ...emptyProduct.description, ...p.description },
      category: p.category,
      brand: p.brand || "",
      image: p.image || "",
      order: p.order || 0,
    });
    setDialogOpen(true);
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/products/${editing.id}`, form);
        toast.success("Ürün güncellendi");
      } else {
        await api.post("/products", form);
        toast.success("Ürün eklendi");
      }
      setDialogOpen(false);
      loadProducts();
    } catch {
      toast.error("Kaydetme başarısız");
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Bu ürünü silmek istediğinize emin misiniz?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Ürün silindi");
    loadProducts();
  };

  const [reseeding, setReseeding] = useState(false);
  const reseedCatalog = async () => {
    if (!window.confirm("Ürün kataloğu varsayılan listeyle yeniden yüklenecek. Mevcut ürün değişiklikleri sıfırlanır (talepler etkilenmez). Devam edilsin mi?")) return;
    setReseeding(true);
    try {
      const { data } = await api.post("/products/reseed");
      toast.success(`Katalog yeniden yüklendi (${data.count} ürün)`);
      loadProducts();
    } catch {
      toast.error("Yeniden yükleme başarısız");
    } finally {
      setReseeding(false);
    }
  };

  const toggleRead = async (inq) => {
    await api.patch(`/inquiries/${inq.id}/read`);
    loadInquiries();
  };
  const deleteInquiry = async (id) => {
    if (!window.confirm("Talebi silmek istediğinize emin misiniz?")) return;
    await api.delete(`/inquiries/${id}`);
    toast.success("Talep silindi");
    loadInquiries();
  };

  const doLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const unread = inquiries.filter((i) => !i.read).length;
  const inputCls = "w-full bg-[#ffffff] border border-black/12 px-3 py-2.5 font-body text-sm text-[#0f1115] focus:border-[#d81f2a] focus:outline-none";

  return (
    <div className="min-h-screen bg-[#ffffff] flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-black/10 bg-[#f6f6f4] hidden md:flex flex-col">
        <div className="p-6 border-b border-black/10">
          <div className="flex items-center gap-1.5">
            <img src="/brand/logo.png" alt="DMD Group logo" className="h-8 w-auto" />
            <span className="font-mono text-[9px] tracking-[0.25em] text-[#d81f2a] mt-1">INT.</span>
          </div>
          <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-[#9aa1ac]">Yönetim Paneli</div>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          <button
            data-testid="tab-products"
            onClick={() => setTab("products")}
            className={`w-full flex items-center gap-3 px-4 py-3 font-mono text-xs uppercase tracking-wider transition-colors ${
              tab === "products" ? "bg-[#d81f2a] text-[#ffffff]" : "text-[#5b6472] hover:text-[#0f1115] hover:bg-black/5"
            }`}
          >
            <Package className="w-4 h-4" /> Ürünler
          </button>
          <button
            data-testid="tab-inquiries"
            onClick={() => setTab("inquiries")}
            className={`w-full flex items-center justify-between px-4 py-3 font-mono text-xs uppercase tracking-wider transition-colors ${
              tab === "inquiries" ? "bg-[#d81f2a] text-[#ffffff]" : "text-[#5b6472] hover:text-[#0f1115] hover:bg-black/5"
            }`}
          >
            <span className="flex items-center gap-3"><Inbox className="w-4 h-4" /> Talepler</span>
            {unread > 0 && (
              <span className="bg-red-500 text-[#0f1115] text-[10px] px-1.5 py-0.5 rounded-full">{unread}</span>
            )}
          </button>
        </nav>
        <div className="p-4 border-t border-black/10">
          <div className="px-4 py-2 font-mono text-[10px] text-[#9aa1ac] truncate">{user?.email}</div>
          <button
            data-testid="logout-btn"
            onClick={doLogout}
            className="w-full flex items-center gap-3 px-4 py-3 font-mono text-xs uppercase tracking-wider text-[#5b6472] hover:text-red-400 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Çıkış
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-auto">
        <header className="border-b border-black/10 px-6 md:px-10 py-6 flex items-center justify-between">
          <h1 className="font-display text-3xl font-bold uppercase tracking-tight text-[#0f1115]">
            {tab === "products" ? "Ürün Yönetimi" : "Gelen Talepler"}
          </h1>
          <div className="flex items-center gap-3">
            <button onClick={doLogout} className="md:hidden font-mono text-xs text-[#5b6472]" data-testid="logout-btn-mobile">Çıkış</button>
            {tab === "products" && (
              <>
                <button
                  data-testid="reseed-catalog-btn"
                  onClick={reseedCatalog}
                  disabled={reseeding}
                  className="inline-flex items-center gap-2 border border-black/15 text-[#0f1115] px-4 py-2.5 font-mono text-xs uppercase tracking-wider hover:border-[#d81f2a] hover:text-[#d81f2a] transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${reseeding ? "animate-spin" : ""}`} /> {reseeding ? "Yükleniyor..." : "Katalogu Yeniden Yükle"}
                </button>
                <button
                  data-testid="add-product-btn"
                  onClick={openNew}
                  className="inline-flex items-center gap-2 bg-[#d81f2a] text-[#ffffff] px-5 py-2.5 font-mono text-xs uppercase tracking-wider font-bold hover:bg-[#0f1115] transition-colors"
                >
                  <Plus className="w-4 h-4" /> Ürün Ekle
                </button>
              </>
            )}
          </div>
        </header>

        <div className="p-6 md:p-10">
          {tab === "products" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="products-list">
              {products.map((p) => (
                <div key={p.id} className="border border-black/10 bg-[#ffffff] overflow-hidden group" data-testid={`admin-product-${p.id}`}>
                  <div className="h-40 overflow-hidden relative">
                    {p.image && <img src={p.image} alt={p.name?.tr} className="w-full h-full object-cover" />}
                    <span className="absolute top-3 left-3 bg-[#ffffff]/80 text-[#d81f2a] px-2 py-1 font-mono text-[10px] uppercase tracking-widest">
                      {p.category}
                    </span>
                  </div>
                  <div className="p-4">
                    <div className="font-display text-lg font-bold uppercase tracking-tight text-[#0f1115] leading-tight">{p.name?.tr}</div>
                    {p.brand && <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-[#5b6472]">{p.brand}</div>}
                    <div className="mt-4 flex gap-2">
                      <button data-testid={`edit-product-${p.id}`} onClick={() => openEdit(p)} className="flex-1 inline-flex items-center justify-center gap-2 border border-black/12 py-2 font-mono text-[10px] uppercase tracking-wider text-[#0f1115] hover:border-[#d81f2a] transition-colors">
                        <Pencil className="w-3 h-3" /> Düzenle
                      </button>
                      <button data-testid={`delete-product-${p.id}`} onClick={() => deleteProduct(p.id)} className="inline-flex items-center justify-center px-3 border border-black/12 text-[#5b6472] hover:border-red-400 hover:text-red-400 transition-colors">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-3" data-testid="inquiries-list">
              {inquiries.length === 0 && (
                <p className="font-mono text-sm text-[#9aa1ac]">Henüz talep yok.</p>
              )}
              {inquiries.map((inq) => (
                <div key={inq.id} data-testid={`inquiry-${inq.id}`} className={`border p-5 ${inq.read ? "border-black/10 bg-[#f6f6f4]" : "border-[#d81f2a]/40 bg-[#ffffff]"}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3">
                        <span className="font-display text-lg font-bold text-[#0f1115]">{inq.name}</span>
                        {!inq.read && <span className="bg-[#d81f2a] text-[#ffffff] px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest">Yeni</span>}
                      </div>
                      <div className="mt-1 font-mono text-xs text-[#5b6472]">
                        {inq.email}{inq.company ? ` · ${inq.company}` : ""}{inq.phone ? ` · ${inq.phone}` : ""}
                      </div>
                      <p className="mt-3 font-body text-sm text-[#374151] max-w-2xl">{inq.message}</p>
                      <div className="mt-2 font-mono text-[10px] text-[#9aa1ac]">{new Date(inq.created_at).toLocaleString("tr-TR")}</div>
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                      <a href={`mailto:${inq.email}`} className="inline-flex items-center justify-center p-2 border border-black/12 text-[#5b6472] hover:text-[#d81f2a] hover:border-[#d81f2a] transition-colors" title="Yanıtla">
                        <ExternalLink className="w-4 h-4" />
                      </a>
                      <button data-testid={`toggle-read-${inq.id}`} onClick={() => toggleRead(inq)} className="inline-flex items-center justify-center p-2 border border-black/12 text-[#5b6472] hover:text-[#0f1115] transition-colors" title="Okundu">
                        {inq.read ? <MailOpen className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
                      </button>
                      <button data-testid={`delete-inquiry-${inq.id}`} onClick={() => deleteInquiry(inq.id)} className="inline-flex items-center justify-center p-2 border border-black/12 text-[#5b6472] hover:text-red-400 hover:border-red-400 transition-colors" title="Sil">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Product dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#ffffff] border-black/10 rounded-none max-w-2xl max-h-[90vh] overflow-y-auto text-[#0f1115]">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-bold uppercase tracking-tight">
              {editing ? "Ürünü Düzenle" : "Yeni Ürün"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={saveProduct} data-testid="product-form" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {["tr", "en", "ru"].map((l) => (
                <div key={l}>
                  <label className="font-mono text-[10px] uppercase tracking-widest text-[#5b6472]">Ad ({l})</label>
                  <input data-testid={`product-name-${l}`} value={form.name[l]} onChange={(e) => setForm({ ...form, name: { ...form.name, [l]: e.target.value } })} className={inputCls} required={l === "tr"} />
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {["tr", "en", "ru"].map((l) => (
                <div key={l}>
                  <label className="font-mono text-[10px] uppercase tracking-widest text-[#5b6472]">Açıklama ({l})</label>
                  <textarea value={form.description[l]} onChange={(e) => setForm({ ...form, description: { ...form.description, [l]: e.target.value } })} rows={2} className={inputCls + " resize-none"} />
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-[#5b6472]">Kategori</label>
                <select data-testid="product-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls}>
                  {CATS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-[#5b6472]">Marka</label>
                <input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-[#5b6472]">Sıra</label>
                <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} className={inputCls} />
              </div>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-[#5b6472]">Görsel</label>
              <div className="mt-1 flex flex-col sm:flex-row gap-3">
                {form.image ? (
                  <img src={form.image} alt="önizleme" className="w-full sm:w-28 h-28 object-cover border border-black/12 shrink-0" data-testid="product-image-preview" />
                ) : (
                  <div className="w-full sm:w-28 h-28 border border-dashed border-black/12 flex items-center justify-center text-[#9aa1ac] shrink-0">
                    <ImageIcon className="w-6 h-6" />
                  </div>
                )}
                <div className="flex-1 space-y-2">
                  <label
                    data-testid="product-image-upload-label"
                    className="inline-flex items-center gap-2 border border-black/12 px-4 py-2.5 font-mono text-[10px] uppercase tracking-wider text-[#0f1115] hover:border-[#d81f2a] cursor-pointer transition-colors"
                  >
                    <Upload className="w-3.5 h-3.5" />
                    {uploading ? "Yükleniyor..." : "Bilgisayardan Yükle"}
                    <input data-testid="product-image-file" type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={uploading} />
                  </label>
                  <input data-testid="product-image" value={form.image?.startsWith("data:") ? "" : form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className={inputCls} placeholder="veya görsel URL yapıştırın (https://...)" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <button type="button" onClick={() => setDialogOpen(false)} className="px-5 py-2.5 font-mono text-xs uppercase tracking-wider text-[#5b6472] hover:text-[#0f1115] transition-colors">İptal</button>
              <button data-testid="save-product-btn" type="submit" className="bg-[#d81f2a] text-[#ffffff] px-6 py-2.5 font-mono text-xs uppercase tracking-wider font-bold hover:bg-white transition-colors">Kaydet</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
